import { useEffect, useRef, useState } from "react";

/**
 * Slideshow playback state for the video preview.
 * Advances scenes synced to audio duration (or a fixed interval when silent).
 * NOTE: the advance effect intentionally depends only on [playing, total];
 * adding `active` would reset the interval on every scene change.
 */
export function useVideoPlayback(total, audioUrl) {
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState(0);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), [timerRef]);

  const start = () => {
    if (audioUrl && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setPlaying(true);
    setActive(0);
  };

  const stop = () => {
    setPlaying(false);
    if (audioRef.current) audioRef.current.pause();
    clearInterval(timerRef.current);
  };

  useEffect(() => {
    if (!playing) return;
    clearInterval(timerRef.current);
    const audio = audioRef.current;
    const perScene =
      audio && audio.duration && isFinite(audio.duration) ? (audio.duration * 1000) / total : 4000;
    timerRef.current = setInterval(() => {
      setActive((a) => {
        if (a + 1 >= total) {
          clearInterval(timerRef.current);
          return a;
        }
        return a + 1;
      });
    }, perScene);
    return () => clearInterval(timerRef.current);
  }, [playing, total]);

  return { playing, active, audioRef, start, stop };
}
